/**
 * AI-Fused High-Risk Hotspots layer (Phase E.2.3) — L.0 layer 3, the visible
 * payoff of the F.1 fusion engine.
 *
 * Fetches /api/high-risk-hotspots for the current bbox (debounced, min_confidence
 * 60) and renders each fused hotspot as the EXACT-ACT DISC marker (icon rebuild Phase 2a,
 * docs/icon-urgency-council-2026-07-05.md §C.1, founder-signed): ONE Cesium billboard whose
 * image is the discGlyphComposite — a solid coloured DISC with the white EXACT `hot-spot`
 * glyph knocked out at centre (icon one-swing #564 — the hotspot family's OWN glyph, not
 * `fire`, which is the OC/reported wildfire family). The disc FILL = Response Level (RESPONSE_LEVEL_COLORS, the panel
 * palette — L1 slate · L2 blue · L3 amber · L4 red) with the disc DIAMETER RETAINED as the
 * redundant level cue. This is the FIRST family on the disc composite; flood/ice/OC/CAP/fusion
 * keep the white-plate CHIP + single map red (ANOMALY_MAP_RED) until Phase 2b (a mixed COP is
 * expected until then). R4: L4 is the single pulsing class — its pulse is a FILL-breathe on
 * billboard.color at a FIXED size (anomalyLevelBillboardColor, §C.1-3), replacing the retired
 * glow-halo entity + size-pulse; L3's amber fill is static. R3/§C.1-2: a WORKED marker
 * (acked / owned / 'response') renders the ACK-STEEL disc static with its size intact (the
 * level survives via size); the lifecycle RING is retired — steel-fill IS the worked cue.
 * 'detected' shows a bare disc (no kind-glyph). Level 3/4 stay visible at country zoom; L1/L2
 * tier in at regional+ (defensive here).
 *
 * #370 DE-FLOOD (founder ruling 2026-06-19): this is the LOCKED urgent surface,
 * narrowed to ONLY the gated reds — it fetches min_response_level=
 * URGENT_MIN_RESPONSE_LEVEL (3), so no L1/L2 reaches the render loop. The demoted
 * L1/L2 mass moved to the default-OFF FusionCandidatesLayer; the two partition
 * every confidence≥60 row exactly. The L1/L2 styling branches below survive only
 * as defensive fallbacks (an out-of-range level still renders muted, never
 * dropped — E.1.7 lesson). See styles/responseLevel + layers/anomalyMarker.
 *
 * Like ActiveFiresLayer, MapView owns the Cesium viewer, so this is an imperative
 * installer returning a handle: MapView calls `requestRefresh` on camera moves,
 * `setVisible` on the toggle, and `pick` from its central click handler to route
 * a hotspot selection to the side panel (HighRiskHotspotBody).
 *
 * Defensive patterns carried from E.2.1: an unmapped/out-of-range response_level
 * renders grey + WARN (never dropped); an older in-flight fetch can't clobber a
 * newer one (loadGen guard); all entities live in one CustomDataSource toggled by
 * `show`.
 */
import * as Cesium from 'cesium';
import type {
  HighRiskHotspotFeature,
  HighRiskHotspotFeatureCollection,
} from '@tsr/shared-types';
import { fetchHighRiskHotspots, type BboxQuery, type HighRiskHotspotFetchOpts } from '../api';
import {
  hotspotLevelStyle,
  isResponseLevel,
  URGENT_MIN_RESPONSE_LEVEL,
  RESPONSE_LEVEL_COLORS,
} from '../styles/responseLevel';
import {
  anomalyLevelBillboardColor,
  anomalyGate,
  anomalyScale,
  isAcknowledged,
  lifecycleStyle,
  liftAckedCountryDrop,
  scaleAnomalyStyle,
  bboxKey,
  ClearedGhostTracker,
  ACK_STEEL,
} from './anomalyMarker';
import { renderDiscGlyph } from '../discGlyphComposite';
import { ownershipRing } from '../styles/ownershipCue';
import { isWithinCanadaScope } from '../canadaScope';
import { incidentLifecycleTreatment } from '../styles/incidentLifecycleStates';
import { urgentRedRegistry, type UrgentRedMarker } from '../greenMotion';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { isHotspotCoLocatedWithOcFul, type OcFulFire } from './coLocatedOcFulSuppression';

const REFRESH_DEBOUNCE_MS = 300;

/** Registry key for the #255 red-yield reporting (matches the ds name). */
const YIELD_LAYER_KEY = 'high-risk-hotspots';

export interface HighRiskHotspotsLayerOptions {
  /** Reports the rendered feature count (for the toggle label). */
  onCount?: (n: number) => void;
  /** Current toggle state — used to set ds.show after a fetch completes. */
  getVisible?: () => boolean;
  /** Marker geometry scale (#132 WALL profile). Default 1 (operator COP). */
  getScale?: () => number;
  /** WALL profile (#272, PART XI Ruling 1.6): lift the Law-3 acked
   *  country-zoom drop so acked-live anomalies pin ACK-STEEL at every wall
   *  zoom. Default false (operator COP — untouched). */
  getLiftAckedGate?: () => boolean;
  /** Phase 5a (council §A "OC-fire unclickable") — the OC-FUL fires currently
   *  rendered (from ActiveFiresLayer). A hotspot dot GENUINELY co-located with
   *  (stacked on) an OC-FUL fire is SUPPRESSED so the fire is the single clickable
   *  marker; the hotspot's data / incident / re-escalation are untouched. Read at
   *  render time; MapView re-renders (reapplySuppression) when the OC-FUL set
   *  changes. Absent → no suppression (backward-compatible). */
  getOcFulFires?: () => readonly OcFulFire[];
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: HighRiskHotspotFetchOpts,
  ) => Promise<HighRiskHotspotFeatureCollection>;
}

export interface HighRiskHotspotsLayerHandle {
  /** Debounced (300ms) bbox-change refresh. */
  requestRefresh(bbox: BboxQuery): void;
  /** Immediate fetch + render (used on first paint / toggle-on). */
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its hotspot feature (null if not ours). */
  pick(entity: unknown): HighRiskHotspotFeature | null;
  /** Phase 5a (council §A) — re-run the last render to re-evaluate OC-FUL
   *  co-location suppression (called by MapView when the OC-FUL fire set
   *  changes). No-op before the first fetch; does not change layer visibility. */
  reapplySuppression(): void;
  destroy(): void;
}

export function installHighRiskHotspotsLayer(
  viewer: Cesium.Viewer,
  opts: HighRiskHotspotsLayerOptions = {},
): HighRiskHotspotsLayerHandle {
  const fetcher = opts.fetcher ?? fetchHighRiskHotspots;
  const ds = new Cesium.CustomDataSource('high-risk-hotspots');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, HighRiskHotspotFeature>();
  const ghosts = new ClearedGhostTracker(viewer, 'high-risk-hotspots');
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;
  // Phase 5a (council §A) — the last rendered collection + its view, so MapView's
  // reapplySuppression() can re-run the render against a changed OC-FUL fire set
  // without a refetch (an OC-FUL fire appearing/departing must re-evaluate suppression).
  let lastFc: HighRiskHotspotFeatureCollection | null = null;
  let lastViewKey = '';

  const render = (fc: HighRiskHotspotFeatureCollection, viewKey: string): void => {
    lastFc = fc;
    lastViewKey = viewKey;
    ds.entities.removeAll();
    entityToFeature.clear();
    // Phase 5a — the OC-FUL fires to suppress co-located dots against (read fresh
    // each render: this is also the path reapplySuppression() takes after an OC-FUL
    // fire appears/departs). Empty when MapView didn't wire it (backward-compatible).
    const ocFulFires = opts.getOcFulFires?.() ?? [];
    const live: Array<{ id: string; position: Cesium.Cartesian3 }> = [];
    // #255 red-yield: collect this render's UNACKED urgents (with each marker's
    // own zoom gate) so green camera motion yields while any is in view.
    const urgent: UrgentRedMarker[] = [];
    for (const feature of fc.features) {
      const level = feature.properties.response_level;
      if (!isResponseLevel(level)) {
        // Out-of-range response_level (shouldn't happen — column is CHECK 1..4)
        // → muted red, regional-gated, but never dropped. WARN so the gap is
        // visible (E.1.7 lesson).
        console.warn(
          `[HighRiskHotspotsLayer] hotspot ${feature.id} has unmapped response_level=${JSON.stringify(
            level,
          )} — rendering muted red`,
        );
      }
      // Colour Law 3: acknowledged (persisted workstate) → ACK-STEEL static, drops
      // from country zoom; otherwise the urgent level style (URGENT-RED, L3/L4 pulse).
      // WALL profile (#132) scales the geometry; hue/lifecycle/motion are preserved.
      // PART XI Ruling 1.6 (wall only): the acked country-drop lifts so ACK-STEEL
      // pins render at every wall zoom.
      const ackStyle = liftAckedCountryDrop(
        lifecycleStyle(hotspotLevelStyle(level), feature.properties),
        feature.properties,
        opts.getLiftAckedGate?.() ?? false,
      );
      // R1/#477 incident-as-aggregator — the wildfire lifecycle transform. When this
      // hotspot is a tracked incident's source, its lifecycle_state drives the marker:
      // detected (plate only) → confirmed (+glyph) → owned (+keyline) → managed
      // (+ack-steel ring, quiet) → resolved (REMOVED). null ⇒ the unchanged base marker.
      const treatment = incidentLifecycleTreatment(
        ackStyle,
        feature.properties.incident_lifecycle_state,
      );
      const [lon, lat] = feature.geometry.coordinates;
      // Canada-scope render guard (finding 1, 2026-07-06 §5.1): never draw a hotspot dot
      // outside Canada — the fetched bbox spans ocean/US at continent zoom and the shared
      // dev DB carries ocean-coordinate test fixtures fused into live RL3/RL4 rows (no
      // country-zoom gate stops them). Skip entirely: no dot, no ghost, no red-yield.
      if (!isWithinCanadaScope(lon, lat)) continue;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      // resolved → leave the live set: ClearedGhostTracker ghosts it ~60s then removes.
      // The incident row persists (replayable via the AAR; reignition re-renders it).
      if (treatment.removeFromLive) continue;
      // R4 (icon-urgency council 2026-07-05, PER-FAMILY scope): L4 is the single pulsing
      // class. Drop L3's size-pulse HERE so the shared LEVEL_DOTS.pulse flags stay
      // untouched — flood/ice/OC/CAP/fusion keep their pre-Phase-2 pulse until Phase 2.
      // A worked (acked/managed) marker is already static, so this is a no-op for it.
      const scaled = scaleAnomalyStyle(treatment.style, opts.getScale?.() ?? 1);
      const style =
        scaled.pulse && scaled.level !== 4
          ? { ...scaled, pulse: false, pulseAmplitude: 0 }
          : scaled;
      // §C.1 EXACT-ACT disc: the disc FILL = Response Level (RESPONSE_LEVEL_COLORS, the panel
      // palette) for an UNWORKED marker, or ACK-STEEL for a WORKED one — the EXACT sheet's
      // ACKED disc. "Worked" = acknowledged / 'response'-quieted (style.color already ACK-STEEL)
      // OR the incident is 'owned'/'response' (treatment.keyline — IC established): §C.1-2
      // "worked/owned states = the steel disc." The steel disc keeps the level's SIZE (R3 —
      // never collapses to L1). The unacked L4 (style.pulse, not worked) is the single pulsing
      // class: it throbs via anomalyLevelBillboardColor's dim↔authored FILL-breathe at a FIXED
      // size (§C.1-3), replacing the retired glow-halo entity + the size-pulse.
      const worked = style.color === ACK_STEEL || treatment.keyline;
      const fillHex = worked
        ? ACK_STEEL
        : isResponseLevel(level)
          ? RESPONSE_LEVEL_COLORS[level]
          : RESPONSE_LEVEL_COLORS[1]; // out-of-range → muted L1 slate (never dropped; WARNed above)
      // Phase 5a (council §A "OC-fire unclickable — named must-fix"): SUPPRESS a
      // hotspot dot GENUINELY co-located with (stacked on) an OC-FUL fire so the fire
      // is the single clickable marker — otherwise the dot renders on top and the
      // topmost-pick click grabs it, and the fire never opens. Keep the id in `live`
      // so the cleared-ghost tracker treats it as PRESENT (NOT a "departed" grey
      // ghost), but draw no dot/ring and don't drive the #255 red-yield: the (static)
      // OC-FUL fire owns this spot. The hotspot's data / incident / re-escalation
      // (realarm.ts) are UNTOUCHED — only the redundant visual dot is hidden. A
      // non-OC-FUL nearby fire (BH/UC/OUT, OC-Monitored) or a distant OC-FUL fire
      // never suppresses — `ocFulFires` is the OC-FUL-only set and the radius is tight
      // (§G). Phase 5c made all stages render the fire glyph; this set is unchanged.
      if (isHotspotCoLocatedWithOcFul(lon, lat, ocFulFires)) {
        live.push({ id: feature.id, position });
        continue;
      }
      // L1/L2 tier in at regional+; L3/L4 are always visible (§3B.8 motion budget).
      const gate = anomalyGate(style);

      // §C.1 (founder re-ruling 2026-07-05): the marker IS ONE billboard — the EXACT-ACT DISC
      // composite (a Response-Level-filled disc with the white EXACT glyph knocked out at
      // centre, discGlyphComposite). The point-dot, the separate CAHS kind-glyph chip, AND the
      // lifecycle-ring entity are ALL RETIRED (§C.1-2: worked/owned = the steel disc, no ring).
      // 'detected' (showKindGlyph=false) → a bare disc (no kind-glyph). Icon one-swing (#564,
      // glyph-precision): the HOTSPOT family renders the `hot-spot` glyph (the EXACT sheet's
      // dedicated "Hot spot (fire intensity)" row), NOT `fire` — `fire` is the OC/reported
      // WILDFIRE family's glyph (ActiveFiresLayer). renderDiscGlyph('hot-spot') never returns
      // null (the glyph is always known); `?? undefined` satisfies the type.
      // Ownership cue (feat/ownership-coordination) — the 3-way viewer-relative keyline baked into
      // the disc; ADDITIVE, never touches fill/size/opacity.
      const ring = ownershipRing(feature.properties.owned_incident) ?? undefined;
      const image =
        renderDiscGlyph('hot-spot', {
          fill: fillHex,
          glyphless: !treatment.showKindGlyph,
          ring,
        }) ?? undefined;
      const ownerName = feature.properties.owned_incident?.lead_organization_name ?? null;
      const dot = ds.entities.add({
        id: `highrisk:${feature.id}`,
        position,
        billboard: {
          image,
          // §C.1-3: FIXED size (no size-grow) — the disc DIAMETER is the redundant level cue
          // (WALL-scaled via scaleAnomalyStyle; #410 country-zoom declutter via scaleByDistance).
          width: style.baseSize,
          height: style.baseSize,
          // Unacked L4 (the single pulsing class) → the dim↔authored fill-breathe; every other
          // marker → WHITE at the level's alpha (identity hue: the baked level/steel fill, static,
          // L1/L2 muted / steel α0.75 — the P1 opacity hierarchy on the disc).
          color: anomalyLevelBillboardColor(!worked && style.pulse, style.alpha),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
          scaleByDistance: anomalyScale(style), // #410: declutter the always-on L3/L4 at country zoom
        },
        // OWNER TAG — the short lead-organization name at regional zoom+, hidden at country, so a
        // duty officer reads WHO holds the incident. Only when owned.
        ...(ownerName
          ? {
              label: {
                text: ownerName,
                font: '600 12px Inter, system-ui, sans-serif',
                fillColor: Cesium.Color.fromCssColorString('#e2e8f0'),
                showBackground: true,
                backgroundColor: Cesium.Color.fromCssColorString('#0b1020').withAlpha(0.75),
                pixelOffset: new Cesium.Cartesian2(0, -(style.baseSize / 2) - 4),
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
                  0,
                  REGIONAL_VISIBLE_FAR_M,
                ),
              },
            }
          : {}),
      });
      entityToFeature.set(dot, feature);
      live.push({ id: feature.id, position });
      // A quiet (managed) marker is ACK-STEEL static — it must NOT drive the #255
      // red-yield (green camera motion only yields for live urgents).
      if (!treatment.quiet && !isAcknowledged(feature.properties)) {
        urgent.push({
          lon,
          lat,
          visibleFarM: style.hideAtCountry ? REGIONAL_VISIBLE_FAR_M : Number.POSITIVE_INFINITY,
        });
      }
    }
    urgentRedRegistry.report(YIELD_LAYER_KEY, urgent);
    // Law 3 cleared-ghost: markers that departed on this same view fade to a grey
    // ghost for ~60s, then auto-leave (a re-appearing id cancels its ghost).
    ghosts.reconcile(live, viewKey);
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      // #370 de-flood: the LOCKED urgent surface is ONLY the gated reds (Response
      // Level ≥ URGENT_MIN_RESPONSE_LEVEL). The demoted L1/L2 mass renders on the
      // default-OFF fusion-candidates layer (FusionCandidatesLayer), so every
      // confidence≥60 row lands on exactly one surface.
      const fc = await fetcher(bbox, ctl.signal, {
        minConfidence: 60,
        minResponseLevel: URGENT_MIN_RESPONSE_LEVEL,
      });
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc, bboxKey(bbox));
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, ds.show);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchHighRiskHotspots failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
      ghosts.setVisible(visible);
      // #255 — a hidden layer's urgents are not in view: release the red-yield.
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, visible);
    },
    pick(entity: unknown): HighRiskHotspotFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    reapplySuppression(): void {
      if (lastFc) render(lastFc, lastViewKey);
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      ghosts.destroy();
      urgentRedRegistry.clear(YIELD_LAYER_KEY);
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
